import type { MotionValue, useDragControls } from 'motion/react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { createContext, useContext } from 'react';

/**
 * The drag controller every surface shares. `x` and `y` are the position of
 * the frame: an offset from the center for a centered surface, absolute
 * pixels for a surface anchored to the viewport origin.
 */
export type SurfaceDrag = {
	/** Horizontal position: an offset from the center, or absolute pixels. */
	x: MotionValue<number>;
	/** Vertical position: an offset from the center, or absolute pixels. */
	y: MotionValue<number>;
	/** Motion drag controls, so the frame can start a drag from its chrome. */
	controls: ReturnType<typeof useDragControls>;
	/** The frame element, needed to measure it against the viewport. */
	frameRef: RefObject<HTMLDivElement | null>;
	/** True while the surface is being dragged. */
	isDragging: boolean;
	/** False when the surface is maximized or explicitly not draggable. */
	isEnabled: boolean;
	/** Starts a drag from a pointer event, ignoring presses on buttons. */
	startDrag: (event: ReactPointerEvent<HTMLElement>) => void;
	/** Pulls the surface back inside the viewport bounds. */
	clampToBounds: () => void;
	/** Returns the surface to its initial position. */
	resetPosition: () => void;
	/** Motion drag start handler. */
	handleDragStart: () => void;
	/** Motion drag end handler, clamps the surface to the viewport. */
	handleDragEnd: () => void;
};

/** What a surface child can read about the surface it sits in. */
export type SurfaceContextValue = {
	/** The drag controller of the surface the caller is rendered in. */
	drag: SurfaceDrag;
	/** True when the surface is maximized. */
	maximized: boolean;
	/** True when the surface can be dragged from its chrome. */
	isDraggable: boolean;
	/** True when this surface is the focused (top-most) window. */
	isFocused: boolean;
};

export const SurfaceContext = createContext<SurfaceContextValue | null>(null);

/** The surface the caller is rendered inside, when there is one. */
export function useSurface() {
	return useContext(SurfaceContext);
}
